import { ReactNode, useMemo, useState } from 'react'
import {
  Box,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { tokens } from '../theme'

export interface QueueColumn<T> {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  /** Vlastní render buňky; default vezme (row as any)[key]. */
  render?: (row: T) => ReactNode
  /** Když chybí, sloupec není řaditelný. */
  getSortValue?: (row: T) => string | number
}

interface QueueTableProps<T> {
  columns: QueueColumn<T>[]
  rows: T[]
  getRowKey: (row: T) => string
  /** Když je zadán, zobrazí se hledací pole a filtruje se přes tento text. */
  getSearchText?: (row: T) => string
  searchPlaceholder?: string
  defaultSortKey?: string
  defaultSortDir?: 'asc' | 'desc'
  emptyText?: string
  /** Filtry vlevo vedle hledání. */
  toolbar?: ReactNode
  onRowClick?: (row: T) => void
}

/** Odstraní diakritiku a sjednotí velikost písmen (hledání i řazení řetězců). */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
}

export function QueueTable<T>({
  columns,
  rows,
  getRowKey,
  getSearchText,
  searchPlaceholder = 'Hledat…',
  defaultSortKey,
  defaultSortDir = 'desc',
  emptyText = 'Žádné záznamy',
  toolbar,
  onRowClick,
}: QueueTableProps<T>) {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<string | undefined>(defaultSortKey)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultSortDir)

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const visibleRows = useMemo(() => {
    let result = rows

    if (getSearchText && search.trim()) {
      const needle = normalize(search.trim())
      result = result.filter((row) => normalize(getSearchText(row)).includes(needle))
    }

    const sortColumn = sortKey ? columns.find((col) => col.key === sortKey) : undefined
    if (sortColumn && sortColumn.getSortValue) {
      const getValue = sortColumn.getSortValue
      const dir = sortDir === 'asc' ? 1 : -1
      // Kopie kvůli stabilitě — vstupní pole nemutujeme.
      result = [...result].sort((a, b) => {
        const av = getValue(a)
        const bv = getValue(b)
        let cmp = 0
        if (typeof av === 'number' && typeof bv === 'number') {
          cmp = av - bv
        } else {
          cmp = String(av).localeCompare(String(bv), 'cs')
        }
        return cmp * dir
      })
    }

    return result
  }, [rows, columns, getSearchText, search, sortKey, sortDir])

  const showToolbar = Boolean(getSearchText || toolbar)

  return (
    <Paper
      elevation={0}
      sx={{
        boxShadow: tokens.shadow.sm,
        borderRadius: `${tokens.radius.lg}px`,
        overflow: 'hidden',
      }}
    >
      {showToolbar && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            flexWrap: 'wrap',
          }}
        >
          {toolbar}
          {getSearchText && (
            <TextField
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              sx={{ ml: toolbar ? 'auto' : 0, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: tokens.neutral[400] }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align ?? 'left'}>
                  {col.getSortValue ? (
                    <TableSortLabel
                      active={sortKey === col.key}
                      direction={sortKey === col.key ? sortDir : 'asc'}
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ color: 'text.secondary', py: 4 }}
                >
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row) => (
                <TableRow
                  key={getRowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:hover': { bgcolor: tokens.neutral[50] },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align ?? 'left'}>
                      {col.render ? col.render(row) : ((row as any)[col.key] as ReactNode)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
